import * as THREE from 'three';
import { FikoSahne } from '../../IFikoThreeJS/FikoSahne';

/**
 * 📏 CATIA-Style Dimension Annotation (Boyut Ölçüsü)
 *
 * Professional dimension measurement annotation with extension lines,
 * arrows, and measurement text.
 *
 * Features:
 * - Extension lines from measurement points
 * - Arrow heads on both ends
 * - Measurement value with unit display
 * - Customizable offset from points
 * - Professional CATIA-style rendering
 *
 * Performance optimizations:
 * - Geometry/Material pooling
 * - Zero allocations in update loop
 * - Canvas texture for dimension text
 */

export interface BoyutOlcusuOptions {
    renk?: string;
    boyut?: number;
    birim?: string;
    offset?: number;
    okBoyutu?: number;
}

export class FikoBoyutOlcusu extends THREE.Group {
    override type = 'FikoBoyutOlcusu';

    // 🔴 SABİT BOYUT - Kamera zoom'una göre scale ayarlanır
    sabitBoyut: boolean = true;
    private hazirlaListenerEklendi: boolean = false;
    private kontrolDegistiEvent = () => this.kameraYonDegisti();

    private baslangic: THREE.Vector3;
    private bitis: THREE.Vector3;
    private renk: string;
    private boyut: number;
    private birim: string;
    private offset: number;
    private okBoyutu: number;

    // Three.js objects
    private dimensionLine: THREE.Line | null = null;
    private extensionLine1: THREE.Line | null = null;
    private extensionLine2: THREE.Line | null = null;
    private arrow1: THREE.Mesh | null = null;
    private arrow2: THREE.Mesh | null = null;
    private textSprite: THREE.Sprite | null = null;

    // Canvas for text rendering
    private canvas: HTMLCanvasElement;
    private context: CanvasRenderingContext2D;
    private texture: THREE.CanvasTexture;

    // Reusable buffers
    private static readonly tempVec3 = new THREE.Vector3();

    constructor(
        public sahne: FikoSahne,
        baslangic: THREE.Vector3,
        bitis: THREE.Vector3,
        ayarlar?: BoyutOlcusuOptions
    ) {
        super();

        this.baslangic = baslangic.clone();
        this.bitis = bitis.clone();
        this.renk = ayarlar?.renk || '#00FFFF';
        this.boyut = ayarlar?.boyut || 12;
        this.birim = ayarlar?.birim || 'mm';
        this.offset = ayarlar?.offset || 0.5;
        this.okBoyutu = ayarlar?.okBoyutu || 0.2;

        // Initialize canvas for text
        this.canvas = document.createElement('canvas');
        this.canvas.width = 128;
        this.canvas.height = 64;
        const ctx = this.canvas.getContext('2d', {
            alpha: true,
            desynchronized: true,
            willReadFrequently: false
        });
        if (!ctx) {
            throw new Error('Failed to get 2D context for FikoBoyutOlcusu');
        }
        this.context = ctx;
        this.texture = new THREE.CanvasTexture(this.canvas);
        this.texture.minFilter = THREE.LinearFilter;
        this.texture.magFilter = THREE.LinearFilter;

        this.name = 'Boyut Ölçüsü';
        this.renderOrder = 999;

        this.init();

        // Sabit boyut için sahneyi hazırla
        if (sahne) {
            this.hazirla(sahne);
        }
    }

    /**
     * 🔴 SABİT BOYUT: Sahneye hazırlama - kamera event listener ekle
     */
    hazirla(sahne?: FikoSahne): void {
        if (!sahne || !sahne.anaGosterim || !sahne.anaGosterim.aktifKamera) {
            return;
        }

        this.sahne = sahne;

        try {
            // İlk scale'i ayarla
            if (this.textSprite) {
                this.textSprite.userData.originalScale = new THREE.Vector3().copy(this.textSprite.scale);
                this.kameraYonDegisti();
            }

            // Kamera değiştiğinde event listener ekle
            if (!this.hazirlaListenerEklendi && sahne.anaGosterim.aktifKontrol) {
                sahne.anaGosterim.aktifKontrol.addEventListener("change", this.kontrolDegistiEvent);
                this.hazirlaListenerEklendi = true;
            }
        } catch (e) {
            /* Ignored */
        }
    }

    /**
     * 🔴 SABİT BOYUT: Kamera zoom değiştiğinde sprite scale'i güncelle
     */
    private kameraYonDegisti(): void {
        if (!this.sabitBoyut || !this.sahne?.anaGosterim?.aktifKamera || !this.textSprite) {
            return;
        }

        const originalScale = this.textSprite.userData.originalScale as THREE.Vector3;
        if (!originalScale) return;

        const kamera = this.sahne.anaGosterim.aktifKamera;
        const zoom = kamera?.zoom ?? 1;
        const factor = (kamera?.top - kamera?.bottom) / zoom;

        // Zoom faktörüne göre scale ayarla
        const scaleAdjustment = factor * 0.02;
        this.textSprite.scale.set(
            originalScale.x * scaleAdjustment,
            originalScale.y * scaleAdjustment,
            1
        );

        this.textSprite.updateMatrix();
    }

    /**
     * 🔴 SABİT BOYUT: Sabit boyut modunu değiştir
     */
    setSabitBoyut(aktif: boolean): void {
        this.sabitBoyut = aktif;
        if (aktif && this.textSprite) {
            this.textSprite.userData.originalScale = new THREE.Vector3().copy(this.textSprite.scale);
            this.kameraYonDegisti();
        }
    }

    private init(): void {
        this.createDimensionGeometry();
        this.updateTextTexture();
    }

    private createDimensionGeometry(): void {
        const distance = this.baslangic.distanceTo(this.bitis);
        const direction = FikoBoyutOlcusu.tempVec3.subVectors(this.bitis, this.baslangic).normalize();

        // Perpendicular direction for offset
        const perpendicular = new THREE.Vector3(-direction.y, direction.x, 0).normalize();

        // Offset points
        const offsetVec = perpendicular.multiplyScalar(this.offset);
        const p1Offset = this.baslangic.clone().add(offsetVec);
        const p2Offset = this.bitis.clone().add(offsetVec);

        // Main dimension line
        const lineMaterial = new THREE.LineBasicMaterial({
            color: new THREE.Color(this.renk),
            linewidth: 2,
            transparent: true,
            opacity: 0.9,
            depthTest: false,
            depthWrite: false
        });

        const lineGeometry = new THREE.BufferGeometry().setFromPoints([p1Offset, p2Offset]);
        this.dimensionLine = new THREE.Line(lineGeometry, lineMaterial);
        this.add(this.dimensionLine);

        // Extension lines
        const ext1Geometry = new THREE.BufferGeometry().setFromPoints([
            this.baslangic,
            p1Offset.clone().add(perpendicular.clone().multiplyScalar(0.2))
        ]);
        this.extensionLine1 = new THREE.Line(ext1Geometry, lineMaterial.clone());
        this.add(this.extensionLine1);

        const ext2Geometry = new THREE.BufferGeometry().setFromPoints([
            this.bitis,
            p2Offset.clone().add(perpendicular.clone().multiplyScalar(0.2))
        ]);
        this.extensionLine2 = new THREE.Line(ext2Geometry, lineMaterial.clone());
        this.add(this.extensionLine2);

        // Arrow heads (simple cones)
        const arrowGeometry = new THREE.ConeGeometry(this.okBoyutu * 0.3, this.okBoyutu, 8);
        const arrowMaterial = new THREE.MeshBasicMaterial({
            color: new THREE.Color(this.renk),
            depthTest: false,
            depthWrite: false
        });

        this.arrow1 = new THREE.Mesh(arrowGeometry, arrowMaterial);
        this.arrow1.position.copy(p1Offset);
        this.arrow1.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), direction);
        this.add(this.arrow1);

        this.arrow2 = new THREE.Mesh(arrowGeometry, arrowMaterial.clone());
        this.arrow2.position.copy(p2Offset);
        this.arrow2.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), direction.negate());
        this.add(this.arrow2);

        // Text sprite at midpoint
        const midpoint = p1Offset.clone().add(p2Offset).multiplyScalar(0.5);

        const spriteMaterial = new THREE.SpriteMaterial({
            map: this.texture,
            transparent: true,
            depthTest: false,
            depthWrite: false
        });

        this.textSprite = new THREE.Sprite(spriteMaterial);
        this.textSprite.position.copy(midpoint);
        this.textSprite.scale.set(2, 1, 1);
        this.add(this.textSprite);
    }

    private updateTextTexture(): void {
        const distance = this.baslangic.distanceTo(this.bitis);
        const text = `${distance.toFixed(2)} ${this.birim}`;

        const ctx = this.context;
        const fontSize = this.boyut;
        ctx.font = `bold ${fontSize}px Arial, sans-serif`;

        const metrics = ctx.measureText(text);
        const textWidth = metrics.width;
        const textHeight = fontSize * 1.5;

        // Power of 2 canvas size with 2x supersampling
        const scale = 2;
        const width = Math.pow(2, Math.ceil(Math.log2(textWidth * scale + 20)));
        const height = Math.pow(2, Math.ceil(Math.log2(textHeight * scale)));

        this.canvas.width = width;
        this.canvas.height = height;

        ctx.setTransform(scale, 0, 0, scale, 0, 0);
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.clearRect(0, 0, width / scale, height / scale);

        // Background
        ctx.fillStyle = 'rgba(0, 26, 26, 0.9)';
        ctx.fillRect(5, 2, textWidth + 10, textHeight);

        // Border
        ctx.strokeStyle = this.renk;
        ctx.lineWidth = 2;
        ctx.strokeRect(5, 2, textWidth + 10, textHeight);

        // Text
        ctx.font = `bold ${fontSize}px Arial, sans-serif`;
        ctx.fillStyle = this.renk;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(text, width / scale / 2, height / scale / 2);

        this.texture.needsUpdate = true;
    }

    /**
     * Update start point
     */
    setBaslangic(point: THREE.Vector3): void {
        this.baslangic.copy(point);
        this.updateGeometry();
    }

    /**
     * Update end point
     */
    setBitis(point: THREE.Vector3): void {
        this.bitis.copy(point);
        this.updateGeometry();
    }

    /**
     * Update color
     */
    setColor(color: string): void {
        this.renk = color;
        if (this.dimensionLine) (this.dimensionLine.material as THREE.LineBasicMaterial).color.set(color);
        if (this.extensionLine1) (this.extensionLine1.material as THREE.LineBasicMaterial).color.set(color);
        if (this.extensionLine2) (this.extensionLine2.material as THREE.LineBasicMaterial).color.set(color);
        if (this.arrow1) (this.arrow1.material as THREE.MeshBasicMaterial).color.set(color);
        if (this.arrow2) (this.arrow2.material as THREE.MeshBasicMaterial).color.set(color);
        this.updateTextTexture();
    }

    /**
     * Update unit
     */
    setUnit(unit: string): void {
        this.birim = unit;
        this.updateTextTexture();
    }

    private updateGeometry(): void {
        // Remove old geometry
        if (this.dimensionLine) this.remove(this.dimensionLine);
        if (this.extensionLine1) this.remove(this.extensionLine1);
        if (this.extensionLine2) this.remove(this.extensionLine2);
        if (this.arrow1) this.remove(this.arrow1);
        if (this.arrow2) this.remove(this.arrow2);
        if (this.textSprite) this.remove(this.textSprite);

        // Recreate
        this.createDimensionGeometry();
        this.updateTextTexture();
    }

    /**
     * Dispose resources
     */
    dispose(): void {
        // 🔴 Event listener temizle
        if (this.sahne?.anaGosterim?.aktifKontrol && this.hazirlaListenerEklendi) {
            this.sahne.anaGosterim.aktifKontrol.removeEventListener("change", this.kontrolDegistiEvent);
            this.hazirlaListenerEklendi = false;
        }

        if (this.dimensionLine) {
            this.dimensionLine.geometry.dispose();
            (this.dimensionLine.material as THREE.Material).dispose();
        }
        if (this.extensionLine1) {
            this.extensionLine1.geometry.dispose();
            (this.extensionLine1.material as THREE.Material).dispose();
        }
        if (this.extensionLine2) {
            this.extensionLine2.geometry.dispose();
            (this.extensionLine2.material as THREE.Material).dispose();
        }
        if (this.arrow1) {
            this.arrow1.geometry.dispose();
            (this.arrow1.material as THREE.Material).dispose();
        }
        if (this.arrow2) {
            this.arrow2.geometry.dispose();
            (this.arrow2.material as THREE.Material).dispose();
        }
        if (this.textSprite) {
            this.textSprite.material.map?.dispose();
            this.textSprite.material.dispose();
        }
        this.texture.dispose();
    }

    override raycast(raycaster: THREE.Raycaster, intersects: THREE.Intersection[]): void {
        if (this.textSprite) {
            this.textSprite.raycast(raycaster, intersects);
        }
    }
}
